import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router";
import type { ClientDTO, KanbanColumnDTO } from "@unik/shared";
import { api } from "../../lib/api";
import { AdminShell } from "../../ui/AdminShell";
import { Button } from "../../ui/Button";
import { EmptyState, Skeleton } from "../../ui/Card";
import { TIER_LABEL } from "../../lib/labels";
import { ColumnModal } from "../../ui/ColumnModal";
import { BriefingDrawer } from "../../ui/BriefingDrawer";
import { QuickClientModal } from "../../ui/QuickClientModal";

export function Clientes() {
  const [clients, setClients] = useState<ClientDTO[] | null>(null);
  const [columns, setColumns] = useState<KanbanColumnDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);

  // Filtros e busca
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"kanban" | "table">("kanban");

  // Modais e Gavetas
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState<KanbanColumnDTO | null>(null);
  const [quickClientModalOpen, setQuickClientModalOpen] = useState(false);
  const [quickClientDefaultStage, setQuickClientDefaultStage] = useState("novo");
  const [briefingTarget, setBriefingTarget] = useState<{
    id: string;
    name: string;
    whatsapp?: string | null;
    business?: string | null;
  } | null>(null);

  // Drag and drop & Feedback
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function loadData() {
    try {
      const [cls, cols] = await Promise.all([
        api.get<ClientDTO[]>("/clients"),
        api.get<KanbanColumnDTO[]>("/kanban/columns"),
      ]);
      setClients(cls);
      setColumns(cols);
    } catch {
      setError("Não foi possível carregar o quadro de gestão.");
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  async function moverEstagio(clientId: string, newStage: string) {
    if (!clients) return;
    const client = clients.find((c) => c.id === clientId);
    if (!client || client.kanbanStage === newStage) return;

    // Atualização otimista imediata na interface
    setClients((prev) =>
      prev
        ? prev.map((c) => (c.id === clientId ? { ...c, kanbanStage: newStage } : c))
        : prev,
    );
    setBusyId(clientId);

    try {
      await api.patch(`/clients/${clientId}/stage`, { stage: newStage });
      showToast("Estágio atualizado com sucesso");
    } catch {
      showToast("Erro ao mover cliente");
      await loadData();
    } finally {
      setBusyId(null);
    }
  }

  async function handleColumnDelete(col: KanbanColumnDTO) {
    const count = (clients ?? []).filter((c) => (c.kanbanStage || "novo") === col.key).length;
    const confirmMsg =
      count > 0
        ? `Excluir o status "${col.label}"? ${count} cliente(s) serão movidos automaticamente para a coluna inicial.`
        : `Deseja realmente remover o status "${col.label}"?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      await api.delete(`/kanban/columns/${col.id}`);
      showToast(`Status "${col.label}" removido`);
      await loadData();
    } catch {
      showToast("Erro ao excluir status");
    }
  }

  async function criarDemo() {
    setSeeding(true);
    try {
      await api.post("/clients/demo", {});
      showToast("Cliente de demonstração adicionado");
      await loadData();
    } catch {
      showToast("Erro ao criar cliente demo");
    } finally {
      setSeeding(false);
    }
  }

  function copiarLinkCadastro() {
    const origin = window.location.origin;
    const link = `${origin}/cadastro`;
    navigator.clipboard.writeText(link);
    showToast("Link de cadastro copiado para o WhatsApp!");
  }

  // Filtragem inteligente
  const filteredClients = useMemo(() => {
    if (!clients) return [];
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      const matchSearch =
        q === "" ||
        c.name.toLowerCase().includes(q) ||
        (c.businessName && c.businessName.toLowerCase().includes(q)) ||
        (c.city && c.city.toLowerCase().includes(q)) ||
        (c.whatsapp && c.whatsapp.includes(q)) ||
        (c.email && c.email.toLowerCase().includes(q));

      const matchTier = tierFilter === "all" || c.tier === tierFilter;
      return matchSearch && matchTier;
    });
  }, [clients, search, tierFilter]);

  // Contadores analíticos
  const totalClients = clients?.length ?? 0;
  const totalBriefings = clients?.filter((c) => c.briefingStatus === "submitted").length ?? 0;
  const totalActive = clients?.filter((c) => c.status === "active").length ?? 0;

  return (
    <AdminShell>
      {/* Toast flutuante */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-[var(--z-toast)] flex items-center gap-2 rounded-[var(--radius-control)] border border-gold/40 bg-surface-raised px-4 py-2.5 text-xs font-medium text-text-cream shadow-2xl backdrop-blur animate-fade-in">
          <span className="text-gold">✦</span>
          <span>{toast}</span>
        </div>
      )}

      {/* Barra de Título e Ações */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="type-h1 text-text-cream">Gestão de Clientes</h1>
              <span className="rounded-full border border-border-default bg-surface-raised px-2.5 py-0.5 text-xs font-medium text-gold">
                {totalClients} clientes
              </span>
            </div>
            <p className="type-caption mt-1 text-text-secondary">
              Quadro integrado com o formulário de cadastro, briefing e esteira de conteúdo.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={copiarLinkCadastro}
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-control)] border border-border-default bg-surface-raised px-3 py-1.5 text-xs font-medium text-text-primary hover:border-gold hover:bg-surface-elevated transition-colors"
            >
              <svg className="h-3.5 w-3.5 fill-current text-gold" viewBox="0 0 24 24">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
              </svg>
              Copiar Link /cadastro
            </button>

            <button
              type="button"
              onClick={() => {
                setQuickClientDefaultStage(columns?.[0]?.key ?? "novo");
                setQuickClientModalOpen(true);
              }}
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-control)] border border-gold/50 bg-gold/15 px-3 py-1.5 text-xs font-semibold text-gold hover:bg-gold/25 hover:border-gold transition-colors shadow-sm"
            >
              <span>+</span> Novo Card
            </button>

            <button
              type="button"
              onClick={() => {
                setEditingColumn(null);
                setColumnModalOpen(true);
              }}
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-control)] border border-border-default bg-surface-raised px-3 py-1.5 text-xs font-medium text-text-primary hover:border-gold hover:bg-surface-elevated transition-colors"
            >
              <span>+</span> Novo Status
            </button>

            <Button size="sm" variant="secondary" onClick={criarDemo} loading={seeding}>
              Cliente Demo
            </Button>

            <Link to="/cadastro">
              <Button size="sm" variant="ghost">Formulário /cadastro</Button>
            </Link>
          </div>
        </div>

        {/* Barra de Filtros e Busca com Fundo Opaco e Blur */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-card)] border border-border-default/80 bg-[#180b0b]/95 p-3.5 backdrop-blur-md shadow-lg">
          <div className="flex flex-1 items-center gap-3 min-w-[240px]">
            <div className="relative flex-1">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome, clínica, WhatsApp ou @Instagram..."
                className="w-full rounded-[var(--radius-control)] border border-border-default bg-[#120606] px-3 py-1.5 text-xs text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2.5 top-1.5 text-xs text-text-tertiary hover:text-text-primary"
                >
                  ✕
                </button>
              )}
            </div>

            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="rounded-[var(--radius-control)] border border-border-default bg-surface-base px-3 py-1.5 text-xs text-text-secondary focus:border-gold focus:outline-none"
            >
              <option value="all">Todas as Ofertas</option>
              <option value="mentoria">Mentoria 1:1</option>
              <option value="consultoria">Consultoria Estratégica</option>
              <option value="imersao">Imersões Privadas</option>
            </select>
          </div>

          <div className="flex items-center gap-2 text-xs text-text-tertiary">
            <span className="hidden sm:inline">
              Briefings: <strong className="text-text-secondary">{totalBriefings}</strong>
            </span>
            <span className="hidden sm:inline">•</span>
            <span className="hidden sm:inline">
              Ativos: <strong className="text-text-secondary">{totalActive}</strong>
            </span>

            <div className="flex rounded-[var(--radius-control)] border border-border-default bg-surface-base p-0.5">
              <button
                type="button"
                onClick={() => setViewMode("kanban")}
                className={`px-2.5 py-1 text-xs rounded transition-colors ${
                  viewMode === "kanban"
                    ? "bg-surface-elevated text-text-cream font-medium shadow-sm"
                    : "text-text-tertiary hover:text-text-secondary"
                }`}
              >
                Kanban
              </button>
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={`px-2.5 py-1 text-xs rounded transition-colors ${
                  viewMode === "table"
                    ? "bg-surface-elevated text-text-cream font-medium shadow-sm"
                    : "text-text-tertiary hover:text-text-secondary"
                }`}
              >
                Lista
              </button>
            </div>
          </div>
        </div>
      </div>

      {error !== null && <p className="type-body mt-6 text-danger">{error}</p>}

      {/* Loading Skeletons */}
      {clients === null && error === null && (
        <div className="mt-6 flex gap-4 overflow-x-auto pb-4">
          <Skeleton className="h-96 w-72 shrink-0" />
          <Skeleton className="h-96 w-72 shrink-0" />
          <Skeleton className="h-96 w-72 shrink-0" />
        </div>
      )}

      {/* Estado Vazio */}
      {clients?.length === 0 && (
        <div className="mt-8">
          <EmptyState
            title="Nenhum cliente cadastrado ainda"
            description="Envie o link do formulário /cadastro ou adicione o primeiro cliente para iniciar."
            action={
              <div className="flex gap-2">
                <Button onClick={copiarLinkCadastro}>Copiar Link /cadastro</Button>
                <Button variant="secondary" onClick={criarDemo} loading={seeding}>
                  Gerar Cliente Demonstração
                </Button>
              </div>
            }
          />
        </div>
      )}

      {/* Modo Kanban */}
      {clients !== null && columns !== null && viewMode === "kanban" && (
        <div className="-mx-5 mt-6 flex gap-4 overflow-x-auto px-5 pb-6 sm:mx-0 sm:px-0">
          {columns.map((col, colIndex) => {
            // Mapeia clientes para a coluna
            const items = filteredClients.filter((c) => {
              const st = c.kanbanStage || "novo";
              // Se a coluna for a primeira ('novo') e o cliente tiver estágio desconhecido, agrupa aqui
              if (colIndex === 0) {
                return st === col.key || !columns.some((k) => k.key === st);
              }
              return st === col.key;
            });

            const isDragOver = dragOverCol === col.key;

            return (
              <div
                key={col.id}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragOverCol !== col.key) setDragOverCol(col.key);
                }}
                onDragLeave={() => {
                  if (dragOverCol === col.key) setDragOverCol(null);
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  setDragOverCol(null);
                  const cid = e.dataTransfer.getData("text/plain") || draggedId;
                  if (cid) {
                    await moverEstagio(cid, col.key);
                  }
                  setDraggedId(null);
                }}
                className={`flex w-84 shrink-0 flex-col rounded-[var(--radius-card)] border transition-all duration-200 shadow-xl backdrop-blur-md ${
                  isDragOver
                    ? "border-gold bg-[#261211]/98 shadow-2xl scale-[1.01] ring-1 ring-gold/40"
                    : "border-border-default/80 bg-[#160a0a]/95"
                }`}
              >
                {/* Topo da Coluna */}
                <div className="flex items-center justify-between border-b border-border-default/60 px-4 py-3 bg-[#200f0e]/95 backdrop-blur-md rounded-t-[var(--radius-card)]">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full shadow-sm"
                      style={{ backgroundColor: col.color }}
                    />
                    <span className="type-label font-semibold text-text-cream">
                      {col.label}
                    </span>
                    <span className="rounded-full bg-surface-elevated px-2 py-0.5 text-[11px] font-mono text-text-tertiary">
                      {items.length}
                    </span>
                  </div>

                  {/* Menu da Coluna e Adicionar Card */}
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setQuickClientDefaultStage(col.key);
                        setQuickClientModalOpen(true);
                      }}
                      title={`Adicionar card em ${col.label}`}
                      className="rounded p-1 text-text-tertiary hover:bg-gold/20 hover:text-gold transition-colors font-bold text-xs"
                    >
                      +
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingColumn(col);
                        setColumnModalOpen(true);
                      }}
                      title="Editar nome e cor deste status"
                      className="rounded p-1 text-text-tertiary hover:bg-surface-elevated hover:text-text-primary transition-colors"
                    >
                      <svg className="h-3.5 w-3.5 fill-current" viewBox="0 0 24 24">
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                      </svg>
                    </button>
                    {columns.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleColumnDelete(col)}
                        title="Remover status"
                        className="rounded p-1 text-text-tertiary hover:bg-danger/20 hover:text-danger transition-colors"
                      >
                        <svg className="h-3.5 w-3.5 fill-current" viewBox="0 0 24 24">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Lista de Cards da Coluna */}
                <div className="flex flex-1 flex-col gap-3 p-3 min-h-[320px]">
                  {items.map((c) => {
                    const rawZap = c.whatsapp ? c.whatsapp.replace(/\D/g, "") : "";
                    const zapLink = rawZap
                      ? `https://wa.me/${rawZap.startsWith("55") ? rawZap : `55${rawZap}`}`
                      : null;

                    const hasBriefing = c.briefingStatus === "submitted";

                    return (
                      <div
                        key={c.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData("text/plain", c.id);
                          setDraggedId(c.id);
                        }}
                        onDragEnd={() => setDraggedId(null)}
                        className={`group relative rounded-[var(--radius-control)] border border-border-default/80 bg-[#241110]/95 p-4 transition-all duration-150 hover:border-gold/60 hover:shadow-lg cursor-grab active:cursor-grabbing backdrop-blur-sm ${
                          busyId === c.id ? "opacity-60 pointer-events-none" : ""
                        } ${draggedId === c.id ? "opacity-40" : ""}`}
                        style={{ borderLeft: `3.5px solid ${col.color}` }}
                      >
                        {/* Cabeçalho do Card: Clique abre o modal de briefing direto */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <button
                              type="button"
                              onClick={() =>
                                setBriefingTarget({
                                  id: c.id,
                                  name: c.name,
                                  whatsapp: c.whatsapp,
                                  business: c.businessName,
                                })
                              }
                              className="type-h3 block truncate text-left font-semibold text-text-cream hover:text-gold transition-colors"
                              title="Clique para abrir o briefing e diagnóstico"
                            >
                              {c.name}
                            </button>
                            {c.businessName && (
                              <p className="type-caption truncate text-text-secondary font-medium">
                                {c.businessName}
                              </p>
                            )}
                          </div>

                          <span className="type-overline shrink-0 rounded bg-surface-base/90 px-2 py-0.5 text-gold border border-border-subtle/80">
                            {TIER_LABEL[c.tier] ?? c.tier}
                          </span>
                        </div>

                        {/* Metadados do Cliente */}
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-text-tertiary">
                          {c.city && (
                            <span className="inline-flex items-center gap-1">
                              📍 {c.city}
                            </span>
                          )}
                          {c.segment && (
                            <span className="truncate max-w-[140px] text-text-tertiary">
                              • {c.segment}
                            </span>
                          )}
                        </div>

                        {/* Badges e Ações Diretas */}
                        <div className="mt-3 flex flex-wrap items-center gap-2 pt-2 border-t border-border-subtle/50 text-xs">
                          {/* Botão Diagnóstico/Briefing */}
                          <button
                            type="button"
                            onClick={() =>
                              setBriefingTarget({
                                id: c.id,
                                name: c.name,
                                whatsapp: c.whatsapp,
                                business: c.businessName,
                              })
                            }
                            className={`inline-flex items-center gap-1 rounded-[var(--radius-control-sm)] px-2.5 py-1 text-[11px] font-medium transition-colors ${
                              hasBriefing
                                ? "bg-emerald-950/60 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-900/60"
                                : "bg-[#140808]/80 text-text-tertiary border border-border-subtle hover:text-text-secondary"
                            }`}
                          >
                            <svg className="h-3 w-3 fill-current" viewBox="0 0 24 24">
                              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                            </svg>
                            {hasBriefing ? "Briefing (22)" : "Briefing"}
                          </button>

                          {/* Link Direto Mapa Mental */}
                          <Link
                            to={`/clientes/${c.id}/mapas`}
                            className="inline-flex items-center gap-1 rounded-[var(--radius-control-sm)] bg-[#140808]/80 border border-border-subtle px-2 py-1 text-[11px] font-medium text-gold hover:border-gold/50 hover:bg-gold/10 transition-colors"
                            title="Abrir Mapa Mental do Cliente"
                          >
                            🗺️ Mapa
                          </Link>

                          {/* WhatsApp Direto */}
                          {zapLink && (
                            <a
                              href={zapLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 rounded-[var(--radius-control-sm)] bg-[#140808]/80 border border-border-subtle px-2 py-1 text-[11px] font-medium text-emerald-400 hover:border-emerald-500/50 hover:bg-emerald-950/30 transition-colors"
                              title="Abrir WhatsApp"
                            >
                              <svg className="h-3 w-3 fill-current" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.301-.15-1.78-.878-2.056-.978-.276-.101-.476-.15-.677.15-.201.3-.777.979-.953 1.18-.175.2-.351.226-.652.076-.301-.15-1.27-.468-2.42-1.493-.894-.798-1.498-1.783-1.674-2.083-.176-.301-.019-.464.132-.614.136-.135.301-.351.451-.527.15-.176.201-.301.301-.502.1-.2.05-.376-.025-.526-.075-.15-.677-1.632-.928-2.235-.244-.587-.492-.507-.677-.517-.175-.01-.376-.01-.577-.01-.201 0-.527.075-.802.376s-1.054 1.029-1.054 2.509c0 1.481 1.079 2.909 1.23 3.109.15.2 2.124 3.243 5.145 4.549.719.311 1.28.497 1.718.636.722.229 1.378.197 1.898.12.579-.087 1.78-.728 2.031-1.431.251-.703.251-1.305.176-1.431-.076-.126-.276-.201-.577-.351z" />
                              </svg>
                              WhatsApp
                            </a>
                          )}

                          {/* Link Esteira */}
                          <Link
                            to={`/clientes/${c.id}/esteira`}
                            className="ml-auto inline-flex items-center gap-1 text-[11px] text-text-tertiary hover:text-gold transition-colors"
                          >
                            Esteira →
                          </Link>
                        </div>

                        {/* Barra de Mudança de Status */}
                        <div className="mt-3 flex items-center justify-between pt-2 border-t border-border-subtle/30 text-xs">
                          <select
                            value={c.kanbanStage || "novo"}
                            onChange={(e) => moverEstagio(c.id, e.target.value)}
                            className="h-7 rounded border border-border-subtle bg-[#120606] px-2 text-[11px] text-text-secondary focus:border-gold focus:outline-none"
                          >
                            {columns.map((k) => (
                              <option key={k.id} value={k.key}>
                                {k.label}
                              </option>
                            ))}
                          </select>

                          <div className="flex items-center gap-1">
                            {colIndex > 0 && (
                              <button
                                type="button"
                                onClick={() => moverEstagio(c.id, columns[colIndex - 1]!.key)}
                                title={`Mover para ${columns[colIndex - 1]!.label}`}
                                className="h-6 w-6 rounded border border-border-subtle bg-surface-base text-text-tertiary hover:text-text-cream hover:border-border-default flex items-center justify-center text-xs"
                              >
                                ←
                              </button>
                            )}
                            {colIndex < columns.length - 1 && (
                              <button
                                type="button"
                                onClick={() => moverEstagio(c.id, columns[colIndex + 1]!.key)}
                                title={`Mover para ${columns[colIndex + 1]!.label}`}
                                className="h-6 w-6 rounded border border-border-subtle bg-surface-base text-text-tertiary hover:text-text-cream hover:border-border-default flex items-center justify-center text-xs"
                              >
                                →
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {items.length === 0 && (
                    <div className="flex flex-1 items-center justify-center rounded-[var(--radius-control)] border border-dashed border-border-subtle/60 p-4 text-center">
                      <p className="type-caption text-text-tertiary">
                        Nenhum cliente neste status
                      </p>
                    </div>
                  )}

                  {/* Botão rápido para adicionar card no rodapé da coluna */}
                  <button
                    type="button"
                    onClick={() => {
                      setQuickClientDefaultStage(col.key);
                      setQuickClientModalOpen(true);
                    }}
                    className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-[var(--radius-control)] border border-dashed border-border-subtle/80 py-2 text-xs font-medium text-text-tertiary hover:border-gold/50 hover:bg-gold/5 hover:text-gold transition-all"
                  >
                    <span>+</span> Adicionar Card
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modo Tabela / Lista */}
      {clients !== null && columns !== null && viewMode === "table" && (
        <div className="mt-6 overflow-hidden rounded-[var(--radius-card)] border border-border-subtle bg-surface-raised">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-border-subtle bg-surface-base text-text-tertiary uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium">Oferta</th>
                <th className="px-4 py-3 font-medium">Status no Kanban</th>
                <th className="px-4 py-3 font-medium">Briefing</th>
                <th className="px-4 py-3 font-medium">Contato</th>
                <th className="px-4 py-3 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle/50">
              {filteredClients.map((c) => {
                const col = columns.find((k) => k.key === (c.kanbanStage || "novo"));
                const rawZap = c.whatsapp ? c.whatsapp.replace(/\D/g, "") : "";
                const zapLink = rawZap
                  ? `https://wa.me/${rawZap.startsWith("55") ? rawZap : `55${rawZap}`}`
                  : null;

                return (
                  <tr key={c.id} className="hover:bg-surface-elevated/50 transition-colors">
                    <td className="px-4 py-3.5">
                      <Link to={`/clientes/${c.id}`} className="font-semibold text-text-cream hover:text-gold">
                        {c.name}
                      </Link>
                      {c.businessName && (
                        <p className="text-text-tertiary text-[11px]">{c.businessName}</p>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-gold font-medium">{TIER_LABEL[c.tier]}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: col?.color ?? "#c2a06b" }}
                        />
                        <select
                          value={c.kanbanStage || "novo"}
                          onChange={(e) => moverEstagio(c.id, e.target.value)}
                          className="rounded border border-border-subtle bg-surface-base px-2 py-1 text-xs text-text-secondary focus:border-gold"
                        >
                          {columns.map((k) => (
                            <option key={k.id} value={k.key}>
                              {k.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <button
                        type="button"
                        onClick={() =>
                          setBriefingTarget({
                            id: c.id,
                            name: c.name,
                            whatsapp: c.whatsapp,
                            business: c.businessName,
                          })
                        }
                        className={`rounded px-2 py-1 text-[11px] font-medium ${
                          c.briefingStatus === "submitted"
                            ? "bg-emerald-950/60 text-emerald-400 border border-emerald-500/30"
                            : "bg-surface-base text-text-tertiary border border-border-subtle"
                        }`}
                      >
                        {c.briefingStatus === "submitted" ? "✓ 22 Respostas" : "Pendente"}
                      </button>
                    </td>
                    <td className="px-4 py-3.5">
                      {zapLink ? (
                        <a
                          href={zapLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-400 hover:underline"
                        >
                          {c.whatsapp}
                        </a>
                      ) : (
                        <span className="text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-right space-x-2">
                      <Link
                        to={`/clientes/${c.id}/esteira`}
                        className="rounded border border-border-default px-2.5 py-1 text-text-secondary hover:text-text-cream hover:bg-surface-elevated"
                      >
                        Esteira
                      </Link>
                      <Link
                        to={`/clientes/${c.id}`}
                        className="rounded border border-border-default px-2.5 py-1 text-text-secondary hover:text-text-cream hover:bg-surface-elevated"
                      >
                        Perfil
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de Status / Coluna */}
      <ColumnModal
        isOpen={columnModalOpen}
        onClose={() => {
          setColumnModalOpen(false);
          setEditingColumn(null);
        }}
        onSaved={loadData}
        columnToEdit={editingColumn}
      />

      {/* Gaveta de Briefing */}
      <BriefingDrawer
        clientId={briefingTarget?.id ?? null}
        clientName={briefingTarget?.name ?? ""}
        clientWhatsapp={briefingTarget?.whatsapp}
        clientBusiness={briefingTarget?.business}
        onClose={() => setBriefingTarget(null)}
      />

      {/* Modal Rápido de Novo Card / Cliente */}
      <QuickClientModal
        isOpen={quickClientModalOpen}
        onClose={() => setQuickClientModalOpen(false)}
        onCreated={loadData}
        defaultStage={quickClientDefaultStage}
        columns={columns ?? []}
      />
    </AdminShell>
  );
}
